import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { 
  CalendarIcon, 
  Plus, 
  X, 
  User, 
  Users, 
  Target, 
  AlertCircle,
  CheckCircle2
} from "lucide-react";
import { Task2 } from "@/types/tasks";
import { toast } from "@/hooks/use-toast";
import { useTasks } from "@/hooks/useTasks";
import { listObras } from "@/lib/obraCache";
import { listMembros, getIniciais } from "@/services/profilesCache";

interface NewTaskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTaskCreated?: (task: Task2) => void;
  defaultWorkId?: string;
}

const priorityOptions = [
  { value: "low", label: "Baixa", color: "bg-gray-100 text-gray-700" },
  { value: "medium", label: "Média", color: "bg-yellow-100 text-yellow-700" },
  { value: "high", label: "Alta", color: "bg-red-100 text-red-700" }
];

const setorOptions = [
  "Fundação",
  "Estrutura", 
  "Alvenaria",
  "Instalações",
  "Acabamento",
  "Qualidade",
  "Segurança",
  "Gerência",
  "Manutenção"
];

export function NewTaskModal({ open, onOpenChange, onTaskCreated, defaultWorkId }: NewTaskModalProps) {
  const { createTask, loading: isCreating } = useTasks();

  // Obras reais (cache hidratado no login) e responsáveis reais (profiles).
  const [obras, setObras] = useState<{ id: string; nome: string }[]>([]);
  const [assigneeOptions, setAssigneeOptions] = useState<{ id: string; name: string }[]>([]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "medium" as "low" | "medium" | "high",
    obraId: "",
    setor: "",
    assigneeId: "",
    startDate: undefined as Date | undefined,
    endDate: undefined as Date | undefined
  });

  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);

  useEffect(() => {
    if (!open) return;
    setObras(listObras().map((o) => ({ id: o.id, nome: o.nomeObra })));
    setFormData((prev) => ({ ...prev, obraId: defaultWorkId ?? prev.obraId }));
    // Membros vêm do cache hidratado no login — mesma fonte usada pelo modal de
    // detalhes, para que a lista de delegação seja idêntica nos dois lugares.
    setAssigneeOptions(listMembros().map((m) => ({ id: m.id, name: m.nome })));
  }, [open, defaultWorkId]);

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      priority: "medium",
      obraId: defaultWorkId ?? "",
      setor: "",
      assigneeId: "",
      startDate: undefined,
      endDate: undefined
    });
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleCreateTask = async () => {
    // Validação básica
    if (!formData.title.trim()) {
      toast({
        title: "Erro",
        description: "O título da tarefa é obrigatório.",
        variant: "destructive",
      });
      return;
    }

    // Prazo anterior ao início era aceito em silêncio e só aparecia torto no
    // Cronograma; agora barra na origem.
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      toast({
        title: "Datas invertidas",
        description: "A data de término não pode ser anterior à data de início.",
        variant: "destructive",
      });
      return;
    }

    try {
      const newTask = await createTask({
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        setor: formData.setor,
        assigneeId: formData.assigneeId || undefined,
        startDate: formData.startDate,
        endDate: formData.endDate,
        workId: formData.obraId || undefined, // vincula a tarefa à obra (work_tags[0])
        projectId: formData.obraId || undefined
      });

      if (newTask) {
        // Callback para notificar criação (a lista se atualiza via refetch)
        if (onTaskCreated) {
          onTaskCreated(newTask);
        }
        handleClose();
      }
    } catch (error) {
      // Error handling is done in the useTasks hook
      console.error('Error creating task:', error);
    }
  };

  const selectedAssignee = assigneeOptions.find(a => a.id === formData.assigneeId);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Nova Tarefa
          </DialogTitle>
          <DialogDescription>
            Preencha os dados da tarefa. Só o título é obrigatório.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Título */}
          <div className="space-y-2">
            <Label htmlFor="title">Título da Tarefa *</Label>
            <Input
              id="title"
              placeholder="Digite o título da tarefa..."
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            />
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              placeholder="Descreva os detalhes da tarefa..."
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={4}
            />
          </div>

          {/* Obra vinculada — define o work_tag usado pela integração com o RDO */}
          <div className="space-y-2">
            <Label>Obra</Label>
            <Select
              value={formData.obraId}
              onValueChange={(value) => setFormData(prev => ({ ...prev, obraId: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione a obra (opcional)" />
              </SelectTrigger>
              <SelectContent>
                {obras.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">Nenhuma obra cadastrada</div>
                ) : (
                  obras.map((obra) => (
                    <SelectItem key={obra.id} value={obra.id}>
                      {obra.nome}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Linha 1: Prioridade e Setor */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prioridade</Label>
              <Select value={formData.priority} onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value as "low" | "medium" | "high" }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priorityOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={option.color}>
                          {option.label}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Setor</Label>
              <Select value={formData.setor} onValueChange={(value) => setFormData(prev => ({ ...prev, setor: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o setor" />
                </SelectTrigger>
                <SelectContent>
                  {setorOptions.map(setor => (
                    <SelectItem key={setor} value={setor}>
                      {setor}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Responsável */}
          <div className="space-y-2">
            <Label>Responsável</Label>
            <Select value={formData.assigneeId} onValueChange={(value) => setFormData(prev => ({ ...prev, assigneeId: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um responsável">
                  {selectedAssignee && (
                    <div className="flex items-center gap-2">
                      <Avatar className="w-6 h-6">
                        <AvatarFallback>{getIniciais(selectedAssignee.name)}</AvatarFallback>
                      </Avatar>
                      <span>{selectedAssignee.name}</span>
                    </div>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {assigneeOptions.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">Nenhum usuário disponível</div>
                ) : (
                  assigneeOptions.map(assignee => (
                    <SelectItem key={assignee.id} value={assignee.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="w-6 h-6">
                          <AvatarFallback>{getIniciais(assignee.name)}</AvatarFallback>
                        </Avatar>
                        <span>{assignee.name}</span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Linha 2: Datas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data de Início</Label>
              <Popover open={showStartDatePicker} onOpenChange={setShowStartDatePicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("justify-start text-left font-normal", !formData.startDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.startDate ? format(formData.startDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.startDate}
                    onSelect={(date) => {
                      setFormData(prev => ({ ...prev, startDate: date }));
                      setShowStartDatePicker(false);
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Data de Término</Label>
              <Popover open={showEndDatePicker} onOpenChange={setShowEndDatePicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("justify-start text-left font-normal", !formData.endDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.endDate ? format(formData.endDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.endDate}
                    onSelect={(date) => {
                      setFormData(prev => ({ ...prev, endDate: date }));
                      setShowEndDatePicker(false);
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={handleClose} disabled={isCreating}>
              Cancelar
            </Button>
            <Button onClick={handleCreateTask} disabled={isCreating} data-testid="criar-tarefa">
              {isCreating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Criando...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Criar Tarefa
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}